import { useEffect } from 'react';
import { useChatStore } from '@/store';
import { videoAPI } from '@/services/api';
import { getPendingTasks, removePendingTask } from '@/utils/taskStorage';
import {
  formatFindSimilarUsersResponse,
  formatSimilarUsersHotResponse,
  formatExtractLeadsResponse,
  formatAnalyzeLeadsResponse,
} from '@/utils/taskFormatter';

export function useTaskRecovery() {
  const { addMessage, updateLastMessage } = useChatStore();

  useEffect(() => {
    const tasks = getPendingTasks();
    if (tasks.length === 0) return;

    tasks.forEach(async (task) => {
      // 如果当前会话里已经有该任务的完成消息，直接清理，避免重复
      const state = useChatStore.getState();
      const currentSession = state.sessions.find((s) => s.id === state.currentSessionId);
      const alreadyCompleted = currentSession?.messages.some(
        (m: any) => m.data?.taskId === task.taskId && m.data?.status === 'completed'
      );
      if (alreadyCompleted) {
        removePendingTask(task.taskId);
        return;
      }

      // 优先更新最后一条属于该任务的消息，避免产生重复的恢复占位消息
      const lastMessage = state.messages[state.messages.length - 1];
      const lastData: any = lastMessage?.data;
      const isLastMessageThisTask = lastData?.taskId === task.taskId && ['pending', 'processing'].includes(lastData?.status);
      if (isLastMessageThisTask) {
        updateLastMessage(
          `⏳ 恢复${task.action}任务，继续查询中...`,
          { action: task.action, taskId: task.taskId, status: 'recovering' },
          'analysis'
        );
      } else {
        addMessage({
          role: 'assistant',
          content: `⏳ 恢复${task.action}任务，继续查询中...`,
          type: 'analysis',
          data: { action: task.action, taskId: task.taskId, status: 'recovering' },
        });
      }

      try {
        const maxAttempts = 3600;
        const interval = 1000;
        let result: any = null;
        let consecutiveErrors = 0;

        for (let i = 0; i < maxAttempts; i++) {
          await new Promise((r) => setTimeout(r, interval));
          const res = await videoAPI.getTaskStatus(task.taskId);
          if (!res.success) {
            // 任务不存在（服务端重启或已清理），立刻放弃
            if (res.code === 404 || res.message?.includes('不存在')) {
              throw new Error('任务已失效，请重新发起');
            }
            consecutiveErrors++;
            if (consecutiveErrors >= 5) {
              throw new Error('任务状态查询失败，请稍后重试');
            }
            continue;
          }
          consecutiveErrors = 0;

          const taskData = res.data;
          if (!taskData) continue;

          if (taskData.status === 'completed') {
            result = taskData.result;
            break;
          } else if (taskData.status === 'failed') {
            const errMsg = taskData.error || taskData.message || '任务执行失败';
            throw new Error(errMsg);
          }

          // 同类热门支持增量展示
          const currentHotVideos = taskData.result?.hotVideos || [];
          if (task.action === '同类热门' && currentHotVideos.length > 0) {
            // 已有部分视频获取到，始终保持展示当前增量结果（避免被纯进度消息覆盖）
            const partialContent = formatSimilarUsersHotResponse(taskData.result);
            updateLastMessage(
              `⏳ ${taskData.message}${taskData.progress !== undefined ? ` (${taskData.progress}%)` : ''}\n\n${partialContent}`,
              {
                action: task.action,
                taskId: task.taskId,
                status: taskData.status,
                progress: taskData.progress,
                hotVideos: currentHotVideos,
                userCount: taskData.result?.users?.length,
              },
              'analysis'
            );
          } else if (taskData.message) {
            // 实时更新进度
            updateLastMessage(
              `⏳ ${taskData.message}${taskData.progress !== undefined ? ` (${taskData.progress}%)` : ''}`,
              {
                action: task.action,
                taskId: task.taskId,
                status: taskData.status,
                progress: taskData.progress,
              },
              'analysis'
            );
          }
        }

        if (!result) {
          throw new Error('轮询超时');
        }

        // 根据 action 类型格式化结果
        let content = '';
        switch (task.action) {
          case '获取同类':
            content = formatFindSimilarUsersResponse(result);
            break;
          case '同类热门':
            content = formatSimilarUsersHotResponse(result);
            break;
          case '评论提炼':
            content = formatExtractLeadsResponse(result);
            break;
          case '评论分析':
            content = formatAnalyzeLeadsResponse(result);
            break;
          case '解析视频': {
            const analysis = result?.analysis;
            content = analysis
              ? `视频分析完成！\n\n` +
                `${analysis.category ? `📂 分类：${analysis.category}\n` : ''}` +
                `${analysis.contentType ? `🎬 内容类型：${analysis.contentType}\n` : ''}` +
                `${analysis.engagementLevel ? `🔥 互动等级：${analysis.engagementLevel}\n` : ''}` +
                `${analysis.summary ? `📝 摘要：${analysis.summary}\n` : ''}` +
                `${analysis.strengths?.length ? `✨ 亮点：${analysis.strengths.join('、')}` : ''}`
              : '视频解析完成！';
            break;
          }
          default:
            content = `${task.action}完成`;
        }

        updateLastMessage(
          content,
          { action: task.action, ...result, taskId: task.taskId, status: 'completed' },
          'analysis'
        );
        removePendingTask(task.taskId);
      } catch (err: any) {
        const msg = err?.message || '未知错误';
        if (msg.includes('取消')) {
          updateLastMessage('❌ 已取消', undefined, 'error');
        } else {
          updateLastMessage(
            `❌ ${task.action}失败：${msg}`,
            undefined,
            'error'
          );
        }
        removePendingTask(task.taskId);
      }
    });
  }, [addMessage, updateLastMessage]);
}
